import { serverGenerates } from "../services/execution.js";
import { sendReport } from "../services/model-actions.js";
import type { CurrentResult, RunProgress } from "../state/store.js";
import { store } from "../state/store.js";
/**
 * ResultView — affichage du résultat courant (image / vidéo / audio) avec
 * infos, avertissements et actions. Branché sur store.currentResult.
 */
import { downloadMenu, imageDownloadOptions } from "./DownloadMenu.js";
import { progressBar } from "./ProgressBar.js";
import { toast } from "./Toast.js";
import { h } from "./dom.js";
import { icon } from "./icons.js";

export interface ResultActions {
	/** Upscale (image) — le résultat courant devient la source. */
	onUpscale?: (result: CurrentResult) => void;
	/** Suppression d'arrière-plan (image). */
	onRemoveBackground?: (result: CurrentResult) => void;
}

export class ResultView {
	readonly element: HTMLElement;
	private viewer: HTMLElement;
	private infoRow: HTMLElement;
	private noticeZone: HTMLElement;
	private actionsRow: HTMLElement;
	private actions: ResultActions;

	constructor(actions: ResultActions = {}) {
		this.actions = actions;
		this.viewer = h("div", { class: "result-viewer" });
		this.infoRow = h("div", { class: "result-info-row" });
		this.noticeZone = h("div", null);
		this.actionsRow = h("div", { class: "result-actions" });
		this.element = h(
			"div",
			{ class: "result-view" },
			this.viewer,
			this.infoRow,
			this.noticeZone,
			this.actionsRow
		);
		store.subscribe(
			(s) => s.currentResult,
			(result) => {
				this.render(result);
				if (result) this.revealIfOutOfSight();
			}
		);
		// Le store garde une entrée par appel à subscribe() : deux tranches
		// demandent deux abonnements.
		store.subscribe(
			(s) => s.run,
			() => this.render(store.get().currentResult)
		);
		// Drawn once now: the subscriptions only answer a change, so the frame
		// used to stay blank until the first generation.
		this.render(store.get().currentResult);
	}

	setActions(actions: ResultActions): void {
		this.actions = actions;
		this.render(store.get().currentResult);
	}

	private render(result: CurrentResult | null): void {
		this.viewer.replaceChildren();
		this.infoRow.replaceChildren();
		this.noticeZone.replaceChildren();
		this.actionsRow.replaceChildren();

		const progress = store.get().run;
		if (progress) {
			this.viewer.appendChild(this.waiting(progress));
			return;
		}

		if (!result) {
			this.viewer.appendChild(
				h(
					"div",
					{ class: "result-placeholder" },
					h("div", { class: "placeholder-icon" }, icon("image")),
					h("small", { class: "help-text" }, "Votre résultat apparaîtra ici.")
				)
			);
			return;
		}

		if (result.error) {
			this.viewer.appendChild(h("div", { class: "result-error" }, result.error));
			// A failure on the person's machine reaches nobody unless they send it.
			if (!serverGenerates()) this.noticeZone.appendChild(reportOffer(result.jobId));
		} else if (result.kind === "image") {
			this.viewer.appendChild(h("img", { src: result.url, alt: result.filename }));
		} else if (result.kind === "video") {
			this.viewer.appendChild(
				h("video", { src: result.url, controls: true, autoplay: true, loop: true })
			);
		} else {
			this.viewer.appendChild(h("audio", { src: result.url, controls: true }));
		}

		if (result.seed !== undefined) this.infoRow.appendChild(h("span", null, `S: ${result.seed}`));
		if (result.generationTime !== undefined)
			this.infoRow.appendChild(h("span", null, `T: ${result.generationTime.toFixed(1)}s`));
		if (result.modelId) this.infoRow.appendChild(h("span", null, `M: ${result.modelId}`));

		if (result.warning) {
			this.noticeZone.appendChild(h("div", { class: "result-warning" }, result.warning));
		}
		if (result.enhancedPrompt) {
			this.noticeZone.appendChild(
				h("p", { class: "help-text" }, h("strong", null, "Prompt étendu : "), result.enhancedPrompt)
			);
		}

		if (!result.error) {
			this.actionsRow.appendChild(
				downloadMenu(
					result.kind === "image"
						? imageDownloadOptions(result.url, result.filename)
						: [{ label: "Télécharger", run: () => this.download(result) }]
				)
			);
			if (result.kind === "image" && this.actions.onUpscale) {
				this.actionsRow.appendChild(
					h(
						"button",
						{
							class: "btn btn-small",
							type: "button",
							onclick: () => this.actions.onUpscale?.(result),
						},
						"Agrandir"
					)
				);
			}
			if (result.kind === "image" && this.actions.onRemoveBackground) {
				this.actionsRow.appendChild(
					h(
						"button",
						{
							class: "btn btn-small",
							type: "button",
							onclick: () => this.actions.onRemoveBackground?.(result),
						},
						"Détourer"
					)
				);
			}
		}
	}

	/**
	 * Amener le résultat sous les yeux quand il n'y est pas.
	 *
	 * Sous 1100px — tout téléphone, toute tablette en portrait — le panneau
	 * droit s'empile sous le formulaire. Mesuré le 2026-09-28 dans une fenêtre
	 * de 500x749 : le panneau commençait à y=922, soit 173 px **sous le bas de
	 * l'écran**. L'image arrivait donc dans une page que personne ne regardait,
	 * ce qui ne se distingue pas d'une génération qui n'aboutit jamais — et
	 * c'est très exactement ce que le propriétaire a vécu sur son téléphone.
	 *
	 * On ne bouge que lorsqu'il y a du neuf à voir, jamais pendant l'attente :
	 * le bouton dit déjà où on en est, là où le doigt vient d'appuyer.
	 *
	 * Déplacement immédiat, et non `behavior: "smooth"` : le défilement doux
	 * n'est pas animé dans le Chrome sans écran qui garde ce comportement
	 * (`tests/test_frontend_boots.py`), et un test qui vérifierait autre chose
	 * que ce qui est livré ne vérifierait rien. Après quatre minutes d'attente,
	 * arriver d'un coup sur son image n'est pas un défaut.
	 */
	private revealIfOutOfSight(): void {
		const rect = this.element.getBoundingClientRect();
		if (rect.top >= 0 && rect.top < window.innerHeight) return;
		this.element.scrollIntoView({ block: "start" });
	}

	/** L'attente, dite à l'écran plutôt que dans un toast qui s'efface. */
	private waiting(progress: RunProgress): HTMLElement {
		if (progress.machineGone) {
			return h(
				"div",
				{ class: "result-placeholder result-stalled" },
				h("div", { class: "placeholder-icon" }, icon("alert")),
				h("div", null, "Votre ordinateur ne répond plus"),
				h(
					"small",
					{ class: "help-text" },
					"Le programme a été fermé, l'ordinateur s'est mis en veille ou la connexion a " +
						"été coupée. Relancez le programme : le site attend qu'il se manifeste, et " +
						"relance la génération de lui-même — cela peut prendre une quinzaine de " +
						"minutes. Pour ne pas attendre, annulez-la dans l'onglet Compte et " +
						"relancez-la."
				)
			);
		}
		// Le chiffre dans la barre, la phrase en dessous : un identifiant de
		// modèle entier dans la barre était coupé au quart.
		const bar = progressBar();
		// readHostedProgress compte en 0-100, progressBar en fraction.
		bar.setProgress(
			progress.percent === null ? 0 : progress.percent / 100,
			progress.percent === null ? "…" : `${Math.round(progress.percent)} %`
		);
		return h(
			"div",
			{ class: "result-placeholder result-waiting" },
			h("div", { class: "placeholder-icon" }, icon("clock")),
			bar.element,
			h("small", { class: "help-text" }, progress.message)
		);
	}

	private download(result: CurrentResult): void {
		const a = document.createElement("a");
		a.href = result.url;
		a.download = result.filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
	}
}

/** « Envoyer le rapport », under a failure: what leaves, and one click. */
function reportOffer(jobId: string | null | undefined): HTMLElement {
	const button = h(
		"button",
		{
			class: "btn btn-small",
			type: "button",
			onclick: async () => {
				button.setAttribute("disabled", "");
				button.textContent = "Envoi du rapport…";
				const failed = await sendReport(jobId);
				if (failed) {
					button.removeAttribute("disabled");
					button.textContent = "Envoyer le rapport";
					toast(failed, "error");
					return;
				}
				button.textContent = "Rapport envoyé";
				toast("Rapport envoyé. Merci : il sert à corriger ce qui a échoué.", "success");
			},
		},
		"Envoyer le rapport"
	);
	return h(
		"div",
		{ class: "result-report" },
		button,
		h(
			"small",
			{ class: "help-text" },
			"Envoie la fin du journal de votre ordinateur, sans mots de passe ni noms de dossiers."
		)
	);
}
